import { Box, CircularProgress, LinearProgress, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDownloadPhotosZipMutation, useLazyGetDownloadJobQuery } from '../../../features/events/eventsApiSlice';
import { DownloadFunctions } from '../DownloadFunctions';

export interface DownloadState {
  selectedImages: string[];
  downloadButtonContent: React.JSX.Element;
  allImagesAreSelectedForDownload: boolean;
  currentDownloadJobId: string | null;
  downloadJobStatus: string | null;
  downloadJobImageCount: number;
  downloadJobProcessedImageCount: number;
  downloadUrlCache: Map<string, string>;
  downloadPhotosZipInProgress: boolean;
  downloadPhotosZipError: any;
}

export interface DownloadActions {
  setSelectedImages: (images: string[]) => void;
  dowloadSelectedImages: () => Promise<void>;
  clearSelectedImages: () => void;
  toggleImageForDownload: (eventId: string, imageId: string) => void;
  handleSelectAllForDownloadCheckBoxChanged: (checked: boolean, eventPhotos: any[]) => void;
}

export const useDownloadManager = (eventPhotos: any[]): [DownloadState, DownloadActions] => {
  const [getDownloadJob] = useLazyGetDownloadJobQuery();
  const [downloadPhotosZip, { isLoading: downloadPhotosZipInProgress, error: downloadPhotosZipError }] =
    useDownloadPhotosZipMutation();

  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [downloadButtonContent, setDownloadButtonContent] = useState<React.ReactElement>(React.createElement(React.Fragment));
  const [allImagesAreSelectedForDownload, setAllImagesAreSelectedForDownload] = useState(false);
  const [currentDownloadJobId, setCurrentDownloadJobId] = useState<string | null>(null);
  const [downloadJobStatus, setDownloadJobStatus] = useState<string | null>(null);
  const [downloadJobImageCount, setDownloadJobImageCount] = useState<number>(0);
  const [downloadJobProcessedImageCount, setDownloadJobProcessedImageCount] = useState<number>(0);
  const [downloadUrlCache, setDownloadUrlCache] = useState<Map<string, string>>(new Map());

  // Helper function to create a cache key from selected images
  const createCacheKey = (imageIds: string[]): string => {
    return imageIds.sort().join('|');
  };

  // Polling function to check download job status
  const pollDownloadJobStatus = async (jobId: string, imageIds: string[]) => {
    let pollingActive = true;

    const poll = async () => {
      if (!pollingActive) return;

      try {
        const response = await getDownloadJob({ jobId });

        if (response.isSuccess && response.data) {
          const job = response.data;
          setDownloadJobStatus(job.state);
          setDownloadJobImageCount(job.imagesToDownloadCount);
          setDownloadJobProcessedImageCount(job.imagesProcessedCount);

          if (job.state === 'completed') {
            pollingActive = false;
            if (job.presignedUrl) {
              const cacheKey = createCacheKey(imageIds);
              setDownloadUrlCache((prev) => new Map(prev).set(cacheKey, job.presignedUrl!));
              DownloadFunctions.triggerDownload(document, job.presignedUrl, 'event-photos.zip');
            }
            setCurrentDownloadJobId(null);
            setDownloadJobStatus(null);
          } else if (job.state === 'failed') {
            pollingActive = false;
            console.error('Download job failed:', job.failureReason);
            alert(`Download failed: ${job.failureReason || 'Unknown error'}`);
            setCurrentDownloadJobId(null);
            setDownloadJobStatus(null);
          } else {
            setTimeout(poll, 1000);
          }
        } else {
          setTimeout(poll, 1000);
        }
      } catch (error) {
        console.error('Error polling download job:', error);
        setTimeout(poll, 1000);
      }
    };

    poll();
    return () => {
      pollingActive = false;
    };
  };

  const handleDownloadZip = async (imageIds: string[]) => {
    try {
      const cacheKey = createCacheKey(imageIds);
      const cachedUrl = downloadUrlCache.get(cacheKey);

      if (cachedUrl) {
        DownloadFunctions.triggerDownload(document, cachedUrl, 'event-photos.zip');
        return;
      }

      const downloadJob = await downloadPhotosZip(imageIds).unwrap();

      if (downloadJob && downloadJob.id) {
        setCurrentDownloadJobId(downloadJob.id);
        setDownloadJobStatus(downloadJob.state);
        pollDownloadJobStatus(downloadJob.id, imageIds);
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const toggleImageForDownload = (eventId: string, imageId: string) => {
    const currentlySelectedImages = [...selectedImages];
    const compoundId = `${eventId}/${imageId}`;

    if (DownloadFunctions.imageIsSelectedForDownload(selectedImages, eventId, imageId)) {
      const index = currentlySelectedImages.indexOf(compoundId);
      if (index >= 0) {
        currentlySelectedImages.splice(index, 1);
      }
    } else {
      currentlySelectedImages.push(compoundId);
    }

    setSelectedImages(currentlySelectedImages);
  };

  const dowloadSelectedImages = async () => {
    await handleDownloadZip(selectedImages);
  };

  const clearSelectedImages = () => {
    setSelectedImages([]);
  };

  const handleSelectAllForDownloadCheckBoxChanged = (checked: boolean, eventPhotos: any[]) => {
    const currentEventImageIds = eventPhotos.map((photo) => `${photo.eventId}/${photo.id}`);

    if (checked) {
      const newSelection = [...selectedImages];
      currentEventImageIds.forEach((imageId) => {
        if (!newSelection.includes(imageId)) {
          newSelection.push(imageId);
        }
      });
      setSelectedImages(newSelection);
    } else {
      const newSelection = selectedImages.filter((imageId) => !currentEventImageIds.includes(imageId));
      setSelectedImages(newSelection);
    }
  };

  // Update all images selected state
  useEffect(() => {
    const currentEventImages = eventPhotos.map((photo) => `${photo.eventId}/${photo.id}`);
    const allCurrentImagesSelected = currentEventImages.every((imageId) => selectedImages.includes(imageId));
    setAllImagesAreSelectedForDownload(allCurrentImagesSelected && currentEventImages.length > 0);
  }, [selectedImages, eventPhotos]);

  // Clear cache entries that don't match current selection
  useEffect(() => {
    if (selectedImages.length > 0) {
      const currentCacheKey = createCacheKey(selectedImages);
      setDownloadUrlCache((prev) => {
        const newCache = new Map();
        if (prev.has(currentCacheKey)) {
          newCache.set(currentCacheKey, prev.get(currentCacheKey)!);
        }
        return newCache;
      });
    } else {
      setDownloadUrlCache(new Map());
    }
  }, [selectedImages]);

  // Update download button content
  useEffect(() => {
    if (downloadPhotosZipInProgress) {
      setDownloadButtonContent(
        React.createElement(
          React.Fragment,
          null,
          React.createElement(CircularProgress, { size: 24 }),
          ' Skapar nedladdningsjobb...'
        )
      );
    } else if (downloadJobStatus) {
      switch (downloadJobStatus) {
        case 'pending':
          setDownloadButtonContent(
            React.createElement(
              React.Fragment,
              null,
              React.createElement(CircularProgress, { size: 24 }),
              ' Förbereder nedladdning...'
            )
          );
          break;
        case 'in-progress-making-zip':
          const progressPercentage =
            downloadJobImageCount > 0 ? Math.round((downloadJobProcessedImageCount / downloadJobImageCount) * 100) : 0;
          setDownloadButtonContent(
            React.createElement(
              Box,
              { sx: { display: 'flex', alignItems: 'center', gap: 1, width: '100%' } },
              React.createElement(CircularProgress, { size: 24 }),
              React.createElement(
                Box,
                { sx: { minWidth: 35 } },
                React.createElement(Typography, { variant: 'body2', color: 'text.secondary' }, `${progressPercentage}%`)
              ),
              React.createElement(
                Box,
                { sx: { width: '100%', mr: 1 } },
                React.createElement(LinearProgress, { variant: 'determinate', value: progressPercentage })
              ),
              React.createElement(
                Typography,
                { variant: 'body2', color: 'text.secondary', sx: { whiteSpace: 'nowrap' } },
                'Förbereder zip-fil'
              )
            )
          );
          break;
        case 'in-progress-storing-zip':
          setDownloadButtonContent(
            React.createElement(React.Fragment, null, React.createElement(CircularProgress, { size: 24 }), ' Snart klart...')
          );
          break;
        case 'completed':
          setDownloadButtonContent(React.createElement(React.Fragment, null, 'Nedladdning klar!'));
          break;
        case 'failed':
          setDownloadButtonContent(React.createElement(React.Fragment, null, 'Nedladdning misslyckades'));
          break;
        default:
          setDownloadButtonContent(
            React.createElement(React.Fragment, null, React.createElement(CircularProgress, { size: 24 }), ' Bearbetar...')
          );
      }
    } else {
      const cacheKey = createCacheKey(selectedImages);
      const hasCachedDownload = downloadUrlCache.has(cacheKey);
      if (hasCachedDownload && selectedImages.length > 0) {
        setDownloadButtonContent(
          React.createElement(
            React.Fragment,
            null,
            `${DownloadFunctions.getDownloadLinkText(selectedImages)} (klar att ladda ned)`
          )
        );
      } else {
        setDownloadButtonContent(
          React.createElement(React.Fragment, null, DownloadFunctions.getDownloadLinkText(selectedImages))
        );
      }
    }
  }, [
    downloadPhotosZipInProgress,
    downloadJobStatus,
    selectedImages,
    downloadJobProcessedImageCount,
    downloadJobImageCount,
    downloadUrlCache,
  ]);

  const state: DownloadState = {
    selectedImages,
    downloadButtonContent,
    allImagesAreSelectedForDownload,
    currentDownloadJobId,
    downloadJobStatus,
    downloadJobImageCount,
    downloadJobProcessedImageCount,
    downloadUrlCache,
    downloadPhotosZipInProgress,
    downloadPhotosZipError,
  };

  const actions: DownloadActions = {
    setSelectedImages,
    dowloadSelectedImages,
    clearSelectedImages,
    toggleImageForDownload,
    handleSelectAllForDownloadCheckBoxChanged,
  };

  return [state, actions];
};
